// src/pages/Diagram.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Diagram.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const apiBase = 'https://digital.tifleducation.com/admin/public/api';

const Diagram = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [diagramLimit, setDiagramLimit] = useState(0);

  // -------------------- FETCH DATA --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return toast.error('User not logged in.');

        const dashRes = await axios.get(`${apiBase}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (dashRes.data?.user) {
          setUser({ ...dashRes.data.user, password: '' });
        }

        const getStoredId = (rawKey) => {
          const raw = localStorage.getItem(rawKey);
          if (!raw) return null;
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.id != null) return parsed.id;
            return parsed;
          } catch {
            return raw;
          }
        };

        const boardId =
          getStoredId('selectedBoardId') ||
          getStoredId('selectedBoard') ||
          getStoredId('board_id') ||
          getStoredId('board');

        const gradeId =
          getStoredId('selectedGradeId') ||
          getStoredId('selectedGrade') ||
          getStoredId('grade_id') ||
          getStoredId('grade');

        const subjectId =
          getStoredId('selectedSubjectId') ||
          getStoredId('selectedSubject') ||
          getStoredId('subject_id') ||
          getStoredId('subject');

        if (!boardId || !gradeId || !subjectId) {
          toast.error("Missing selected board/grade/subject. Please go back and select.");
          return;
        }

        if (!boardId || !gradeId || !subjectId) return toast.error('Missing board/grade/subject.');

        const res = await axios.post(
          `${apiBase}/papers/fetch-questions`,
          { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const diagramQs = [];
        Object.entries(res.data || {}).forEach(([chapterId, chapterData]) => {
          const arr = chapterData.types?.['Diagram-Based_Image-Based Questions'] || [];
          (Array.isArray(arr) ? arr : []).forEach(q => diagramQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) }));
        });
        setQuestions(diagramQs);

        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.values(saved).forEach(ch => {
          const arr = ch.diagram_image_based || [];
          arr.forEach(id => selectedMap[Number(id)] = true);
        });
        setSelectedQuestions(selectedMap);

        const regularForm = JSON.parse(localStorage.getItem('regularPaperForm') || '{}');
        setDiagramLimit(Number(regularForm.diagram_image_based || 0));


      } catch (error) {
        console.error('Error fetching diagram questions:', error.response?.data || error);
        toast.error('Failed to fetch diagram questions.');
      }
    };
    fetchData();
  }, []);

  // -------------------- SELECT QUESTIONS --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!diagramLimit) return toast.error('No diagram limit set.');
    if (!alreadySelected && currentCount >= diagramLimit) return toast.error(`You can only select up to ${diagramLimit} diagram questions.`);

    const nextSelected = { ...selectedQuestions };
    if (nextSelected[qId]) delete nextSelected[qId];
    else nextSelected[qId] = true;
    setSelectedQuestions(nextSelected);

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].diagram_image_based)) saved[chapterKey].diagram_image_based = [];

      const arr = saved[chapterKey].diagram_image_based;
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));
      saved[chapterKey].diagram_image_based = arr;

      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/papers/save-questions`,
        { chapter_id: Number(chapterId), type: "diagram_image_based", selected_questions: arr },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Selection saved');
    } catch (error) {
      console.error('Error saving diagram selection:', error.response?.data || error);
      toast.error('Failed to save selection');
    }
  };

  // -------------------- PROFILE SAVE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} activeItem={activeItem} setActiveItem={setActiveItem} />

      <main className="main-content">
        <div className="diagram-header">
          <div className="diagram-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link"><i className="fa fa-arrow-left" /></a>
            <span>Diagram Based Questions</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <FaUserCircle className="icon" onClick={() => setIsModalOpen(true)} />
          </div>
        </div>

        <div className="diagram-questions-container">
          {questions.length > 0 ? questions.map((q, index) => {
            const htmlWithImgStyle = (q.question_title || '').replace(
              /<img /g,
              '<img style="height:150px; vertical-align:middle; margin-left:5px;" '
            );
            return (
              <div key={q.id} className="diagram-question-box">
                <div className="diagram-question" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    type="checkbox"
                    className="diagram-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  />
                  <span className="question-text" style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                    {index + 1}. <span dangerouslySetInnerHTML={{ __html: htmlWithImgStyle }} />
                  </span>
                </div>
              </div>
            );
          }) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Diagram Questions Found</h3>
              <p style={{ color: '#666' }}>There are currently no diagram-based questions for this selection.</p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default Diagram;
